import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'

/** AP-35 Detailblatt 12 — das LV im Projekt (`/projects/:id/lv`), Baustein `PROJECT_MGMT.IMPORT`. */
const basis = (projectId: string) => `/projects/${projectId}/lv`

export type LvZeichensatz = 'cp850' | 'latin1' | 'utf-8'
export type LvZuordnung = 'ASSIGNED' | 'PARTIAL' | 'OVER' | 'NONE' | 'TEXT'
export type LvArt = 'NORMAL' | 'OPTIONAL' | 'ALTERNATIVE' | 'REGIE' | 'SURCHARGE' | 'TEXT'

export interface ProjectLvHead {
  id: string
  fileName: string
  format: string
  formatVersion: string
  phase: string
  title: string
  lvDate: string | null
  hasPrices: boolean
  version: number
  isCurrent: boolean
  previousLvId: string | null
  importedAt: string
  projectFileId: string | null
  /** Verkaufszeilen, die noch auf eine ältere Version zeigen. */
  changedLines: number
  counts: {
    items: number
    assigned: number
    partial: number
    over: number
    none: number
    text: number
    optional: number
    alternative: number
    regie: number
    surcharge: number
    unitsUnknown: number
  }
}

export interface ProjectLvGroup {
  id: string
  parentId: string | null
  oz: string
  name: string
  level: number
}

export interface ProjectLvItem {
  id: string
  groupId: string | null
  oz: string
  kind: LvArt
  shortText: string
  longText: string | null
  introText: string | null
  quantity: number | null
  unitRaw: string | null
  unitId: string | null
  unitCode: string | null
  parentOz: string | null
  surchargePercent: number | null
  /** Nur mit `projects.price.view`. */
  unitPrice?: number | null
  total?: number | null
  gapText: string | null
  lbRef: string | null
  assignedQuantity: number
  restQuantity: number | null
  state: LvZuordnung
  usedIn: {
    salesLineId: string
    positionId: string
    positionNumber: number
    positionName: string
    quantity: number
    unitCode: string | null
    state: string
    outdated: boolean
  }[]
}

export interface ProjectLvStatRow {
  itemId: string
  groupId: string | null
  oz: string
  kind: LvArt
  shortText: string
  unitCode: string | null
  lvQuantity: number
  inSales: number
  inSalesPercent: number | null
  delivered?: number
  invoiced?: number
  invoicedPercent?: number | null
  rest: number
  over: boolean
  lvAmount?: number | null
  salesAmount?: number
  invoicedAmount?: number
}

export interface ProjectLvStatistics {
  lv: { id: string; version: number; title: string; hasPrices: boolean; isCurrent: boolean }
  billing: boolean
  prices: boolean
  groups: ProjectLvGroup[]
  rows: ProjectLvStatRow[]
  totals: {
    items: number
    withRest: number
    over: number
    lvAmount?: number | null
    salesAmount?: number
    invoicedAmount?: number
    inSalesPercent: number | null
    invoicedPercent?: number | null
  }
  supplements: {
    rows: { positionId: string; number: number; supplementNo: number | null; name: string; lines: number; quantity: number; amount?: number; approvedAt: string | null; state: string }[]
    count: number
    approved: number
    amount?: number | null
    approvedAmount?: number
    shareOfLv?: number | null
  }
  free: { lines: number; amount?: number }
}

export interface ProjectLvPreview {
  format: string
  formatVersion: string
  phase: string
  encoding: string
  title: string
  project: string | null
  client: string | null
  lvDate: string | null
  hasPrices: boolean
  stats: { items: number; groups: number; gaps: number; byKind: Record<string, number> }
  groups: { oz: string; name: string; items: number; groups: ProjectLvPreview['groups'] }[]
  units: { unit: string; count: number; unitCode: string | null }[]
  notes: { code: string; text: string }[]
  asVersion: number | null
  comparison: {
    changed: number
    new: number
    removed: number
    unchanged: number
    lines: { oz: string; status: 'CHANGED' | 'NEW' | 'REMOVED'; shortText: string; changes: { field: string; before: unknown; after: unknown }[] }[]
  } | null
}

const formular = (datei: File, felder: Record<string, string | undefined>) => {
  const f = new FormData()
  f.append('file', datei)
  for (const [k, v] of Object.entries(felder)) if (v !== undefined) f.append(k, v)
  return f
}

export const fetchProjectLvs = (projectId: string) => api.get<{ data: ProjectLvHead[] }>(basis(projectId)).then((r) => r.data.data)

export const inspectProjectLv = (projectId: string, datei: File, opts: { encoding?: LvZeichensatz; asNewLv?: boolean }) =>
  api
    .post<{ data: ProjectLvPreview }>(`${basis(projectId)}/inspect`, formular(datei, { encoding: opts.encoding, asNewLv: opts.asNewLv ? 'true' : undefined }))
    .then((r) => r.data.data)

export const importProjectLv = (projectId: string, datei: File, opts: { encoding?: LvZeichensatz; asNewLv?: boolean; headFields?: boolean }) =>
  api
    .post<{ data: ProjectLvHead }>(
      basis(projectId),
      formular(datei, { encoding: opts.encoding, asNewLv: opts.asNewLv ? 'true' : undefined, headFields: opts.headFields ? 'true' : undefined }),
    )
    .then((r) => r.data.data)

export const fetchProjectLvItems = (projectId: string, lvId: string) =>
  api.get<{ data: { lv: ProjectLvHead; groups: ProjectLvGroup[]; items: ProjectLvItem[] } }>(`${basis(projectId)}/${lvId}/items`).then((r) => r.data.data)

export const fetchProjectLvStatistics = (projectId: string, lvId: string) =>
  api.get<{ data: ProjectLvStatistics }>(`${basis(projectId)}/${lvId}/statistics`).then((r) => r.data.data)

export const projectLvStatisticsExport = (projectId: string, lvId: string, params: Record<string, unknown>): ExportRequest => ({
  url: `${basis(projectId)}/${lvId}/statistics/export`,
  params,
})

export const copyProjectLvItems = (projectId: string, lvId: string, itemIds: string[]) =>
  api.post<{ data: unknown[]; skippedText: number }>(`${basis(projectId)}/${lvId}/items/copy`, { itemIds }).then((r) => r.data)

export const setProjectLvUnit = (projectId: string, lvId: string, itemId: string, dto: { unitId: string | null; remember: boolean }) =>
  api.patch<{ data: { updated: number } }>(`${basis(projectId)}/${lvId}/items/${itemId}/unit`, dto).then((r) => r.data.data)

export const deleteProjectLv = (projectId: string, lvId: string) => api.delete(`${basis(projectId)}/${lvId}`)

export const applyAllLvChanges = (projectId: string, lvId: string) =>
  api.post<{ data: { applied: number; skipped: number } }>(`${basis(projectId)}/${lvId}/apply-changes`).then((r) => r.data.data)

export const applyLvChange = (salesLineId: string) => api.post(`/projects/sales-lines/${salesLineId}/apply-lv-change`).then((r) => r.data)

export const lvItemToJournal = (projectId: string, lvId: string, itemId: string, typeId: string) =>
  api.post(`${basis(projectId)}/${lvId}/items/${itemId}/to-journal`, { typeId }).then((r) => r.data)
